/**
 * Ball geometry and colour. Shared by the create/crop UI (which renders preview
 * circles and exports crops at the right resolution) and the game renderer, so the
 * two can never disagree about how big ball #7 is or what colour its ring is.
 *
 * "Rank" is 1-indexed throughout, matching what the user sees on the crop screen.
 */

export const MIN_BALLS = 3;
export const MAX_BALLS = 15;

/**
 * Radius of the smallest and largest ball, as a fraction of board width.
 *
 * These set how many balls fit across the board, which is what decides whether the
 * game is about stacking or about spreading out. At 0.045 roughly eleven of the
 * smallest sat side by side and little ever piled up; Suika fits about six.
 *
 * Widening the ladder and narrowing the board pull against each other: keeping the old
 * 5.2x spread while fitting only seven across would need a largest ball 71% of the
 * board wide, which could never meet another of its own size. 0.068–0.245 is the
 * balance — about six and a half smallest across, two largest across, 3.2x end to
 * end. Two of the largest only just fit side by side, which is the hard ceiling: any
 * wider and the top rank could never meet its own kind.
 */
const MIN_RADIUS_RATIO = 0.077;
const MAX_RADIUS_RATIO = 0.249;

/**
 * Radii for every rank, as a fraction of board width, in a geometric progression.
 *
 * The endpoints are fixed and the growth factor is derived from the count, so a
 * 15-ball game steps ~11% per rank and an 8-ball game steps ~22%. Sizes stay
 * "slightly increasing" (the user-facing promise) while the largest ball always
 * fills the same share of the board regardless of N.
 */
export function ballRadiusRatios(count: number): number[] {
  if (count < 1) return [];
  if (count === 1) return [MAX_RADIUS_RATIO];
  const growth = Math.pow(MAX_RADIUS_RATIO / MIN_RADIUS_RATIO, 1 / (count - 1));
  return Array.from({ length: count }, (_, i) => MIN_RADIUS_RATIO * Math.pow(growth, i));
}

/** Absolute radii in world units for a board of the given width. */
export function ballRadii(count: number, boardWidth: number): number[] {
  return ballRadiusRatios(count).map((r) => r * boardWidth);
}

const START_HUE = 8; // warm coral, matching the app's primary

/**
 * Hue for every rank, given how many balls are in the set.
 *
 * Two things matter and they pull in opposite directions: every ball on the board
 * should be distinguishable from every other, and *consecutive* ranks especially so,
 * since those are the pairs you must tell apart to know what merges with what.
 *
 * So: take `count` evenly-spaced hues, which is the maximum possible separation for
 * that many colours, then hand them out by interleaving the two halves of the wheel.
 * Consecutive ranks land ~180° apart while no two balls are closer than 360/count.
 *
 * (A golden-angle rotation gets the consecutive-rank part right but wraps after ~11
 * steps, which put rank 1 and rank 14 only 12° apart in a full 15-ball set.)
 */
export function ballHues(count: number): number[] {
  const step = 360 / count;
  const half = Math.ceil(count / 2);
  const slots: number[] = [];
  for (let i = 0; i < half; i++) {
    slots.push(i);
    if (i + half < count) slots.push(i + half);
  }
  return slots.map((slot) => (START_HUE + slot * step) % 360);
}

/**
 * HSL is not perceptually uniform: at a fixed lightness, yellows read washed-out and
 * muddy while blues read nearly black. Tilting lightness and saturation along the hue
 * wheel evens that out, so every ring reads with the same visual weight.
 *
 * `yellowness` is +1 around hue 75 (yellow) and -1 around hue 255 (blue).
 */
function balanced(hue: number, lightness: number, saturation: number): string {
  const yellowness = Math.cos(((hue - 75) * Math.PI) / 180);
  const l = lightness + 6 * yellowness;
  const s = saturation - 5 * yellowness;
  return `hsl(${hue.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;
}

/** Ring colour for a rank — the thick border that identifies each ball. */
export function ballColor(rank: number, count: number): string {
  return balanced(ballHues(count)[rank - 1] ?? START_HUE, 58, 72);
}

/** Darker variant of the ring colour, for the outer edge and inner shading pass. */
export function ballColorDark(rank: number, count: number): string {
  return balanced(ballHues(count)[rank - 1] ?? START_HUE, 44, 64);
}

/**
 * How many of the smallest ranks are allowed to drop from the top.
 *
 * Suika drops its 5 smallest fruits out of 11, so the ladder always has headroom to
 * work toward. Scaling by count keeps that proportion for smaller sets.
 */
export function droppableRankCount(count: number): number {
  return Math.max(1, Math.min(5, Math.ceil(count / 2.5)));
}

/**
 * Points awarded for merging two balls of the given rank.
 *
 * Triangular numbers, exactly like Suika: 1, 3, 6, 10, 15, 21, 28, 36, 45, 55, 66...
 * Merging rank n produces one ball of rank n+1.
 */
export function mergeScore(rank: number): number {
  return (rank * (rank + 1)) / 2;
}

/**
 * Ring thickness for a ball, in world units. Grows with the ball but sub-linearly,
 * so small balls still read as distinctly coloured and big ones aren't overwhelmed.
 */
export function ringWidth(radius: number): number {
  return Math.max(2.5, radius * 0.16);
}
